import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

/**
 * Displays submit validation errors as a navigable sequence.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string[]} props.errors
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
const ValidationErrorsDialog = ({ open, errors = [], onClose }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevOpen, setPrevOpen] = useState(open);

  // When open transitions from false to true, reset the active index.
  // This is the React-recommended pattern for deriving state from a prop change
  // without an effect (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setActiveIndex(0);
    }
  }

  const normalizedErrors = useMemo(
    () => errors.filter((error) => typeof error === "string" && error.trim()),
    [errors]
  );

  const totalErrors = normalizedErrors.length;
  const hasErrors = totalErrors > 0;
  const safeIndex = hasErrors ? Math.min(activeIndex, totalErrors - 1) : 0;

  // Clamp index in case the errors array shrinks while the dialog is open.
  if (safeIndex !== activeIndex) {
    setActiveIndex(safeIndex);
  }

  const currentMessage = hasErrors
    ? normalizedErrors[safeIndex]
    : "No validation errors.";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Validation Errors</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="validation-errors-position"
          >
            {hasErrors ? `${safeIndex + 1} / ${totalErrors}` : "0 / 0"}
          </Typography>
        </Box>
        <Typography data-testid="validation-errors-message">
          {currentMessage}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setActiveIndex((current) => Math.max(0, current - 1))}
          disabled={!hasErrors || safeIndex === 0}
        >
          PREVIOUS
        </Button>
        <Button
          variant="outlined"
          onClick={() =>
            setActiveIndex((current) => Math.min(totalErrors - 1, current + 1))
          }
          disabled={!hasErrors || safeIndex >= totalErrors - 1}
        >
          NEXT
        </Button>
        <Button variant="contained" onClick={onClose}>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidationErrorsDialog;
